import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface SobrietyTracker {
  id: string;
  substance: string;
  start_date: string;
  daily_cost_euros: number;
  daily_time_minutes: number;
  created_at: string;
}

const SUBSTANCES = [
  "Alcool", "Tabac", "Cannabis", "Cocaïne", "Opioïdes",
  "Amphétamines", "Benzodiazépines", "Ecstasy/MDMA", "GHB/GBL",
  "Jeux d'argent", "Écrans", "Autre"
] as const;

export type SubstanceName = typeof SUBSTANCES[number];
export { SUBSTANCES };

export interface MilestoneInfo {
  label: string;
  days: number;
  emoji: string;
}

export const MILESTONES: MilestoneInfo[] = [
  { label: "1 jour", days: 1, emoji: "🌱" },
  { label: "3 jours", days: 3, emoji: "💪" },
  { label: "1 semaine", days: 7, emoji: "⭐" },
  { label: "2 semaines", days: 14, emoji: "🔥" },
  { label: "1 mois", days: 30, emoji: "🏆" },
  { label: "2 mois", days: 60, emoji: "💎" },
  { label: "3 mois", days: 90, emoji: "🎯" },
  { label: "6 mois", days: 180, emoji: "🌟" },
  { label: "1 an", days: 365, emoji: "👑" },
  { label: "2 ans", days: 730, emoji: "🦅" },
];

export function getNextMilestone(days: number): MilestoneInfo | null {
  return MILESTONES.find((m) => m.days > days) ?? null;
}

export function getReachedMilestones(days: number): MilestoneInfo[] {
  return MILESTONES.filter((m) => m.days <= days);
}

export function useSobrietyTracker() {
  const { user } = useAuth();
  const [trackers, setTrackers] = useState<SobrietyTracker[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTrackers = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("sobriety_trackers")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: true });
    setTrackers((data as SobrietyTracker[]) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchTrackers();
  }, [fetchTrackers]);

  const addTracker = async (
    substance: string,
    startDate: string,
    dailyCost: number = 0,
    dailyTime: number = 0
  ) => {
    if (!user) return;
    const { error } = await supabase.from("sobriety_trackers").insert({
      user_id: user.id,
      substance,
      start_date: startDate,
      daily_cost_euros: dailyCost,
      daily_time_minutes: dailyTime,
    });
    if (!error) await fetchTrackers();
    return error;
  };

  const resetTracker = async (id: string) => {
    const { error } = await supabase
      .from("sobriety_trackers")
      .update({ start_date: new Date().toISOString() })
      .eq("id", id);
    if (!error) await fetchTrackers();
    return error;
  };

  const deleteTracker = async (id: string) => {
    const { error } = await supabase
      .from("sobriety_trackers")
      .delete()
      .eq("id", id);
    if (!error) await fetchTrackers();
    return error;
  };

  const updateTracker = async (
    id: string,
    updates: Partial<Pick<SobrietyTracker, "daily_cost_euros" | "daily_time_minutes">>
  ) => {
    const { error } = await supabase
      .from("sobriety_trackers")
      .update(updates)
      .eq("id", id);
    if (!error) await fetchTrackers();
    return error;
  };

  return { trackers, loading, addTracker, resetTracker, deleteTracker, updateTracker, refetch: fetchTrackers };
}
